import React from "react"
import { useParams } from "react-router-dom"
import { Form, Alert } from "antd"
// @ts-ignore
import ModalForm from "components/Modal/ModalForm/ModalForm"
// @ts-ignore
import { Select } from "components-design-system"
import Label from "components/Label/Label"
import Separator from "components/Separator/Separator"
import ActivityListItem, { ActivityStatusBadge } from "./ActivityListItem/ActivityListItem"
import AttachActivityItem from "./AttachActivityItem/AttachActivityItem"
import CreateActivityItem from "./CreateActivityItem/CreateActivityItem"
import "./LinkActivitiesModal.scss"

// api
// @ts-ignore
import { useGetActivityTypes } from "api/hooks/activities/useActivitiesApi"
// @ts-ignore
import { useGetStudyActivities } from "api/hooks/studies/useActivitiesApi"
import { usePostAssessmentActivity } from "api/hooks/studies/useAssessmentsApi"

// types
import type { Assessment, LinkedActivity, ActivityStatus as ProgressStatus, ActivityType } from "../assessment-types"

export type StudyActivity = {
  id: number
  project_title: string
  project_type_title: string
  actual_completion_date: string
  progress_status: ProgressStatus
  curr_state: string // "Cancelled"
}

export type PayloadAttach = {
  project_id: number
  name: string | null
}

export type PayloadCreate = {
  project_type_id: number
  name: string | null
  should_complete: boolean
}

// payload type
type Payload = {
  detach_project_ids: number[]
  attach_projects: PayloadAttach[]
  create_project_types: PayloadCreate[]
}

export type ActivityTypeKey = "linked" | "created" | "new"

export type ActivityStatus = "New" | "In Progress" | "Completed" | "Cancelled"

type ActivitySelectOption = {
  value: string // "linked-id" or "created-id" or "new-id"
  label: string
}

type LinkActivitiesModalProps = {
  open: boolean
  onCancel: () => void
  assessment: Assessment
  onCreated: () => void
}

// constants

const defaultSelectedActivities = {
  create_project_types: [],
  attach_projects: [],
  detach_project_ids: [],
}

const RENAME_ERROR_MSG = "This Activity Name already exists in the study. It cannot be duplicated."

const LinkActivitiesModal = ({ open, onCancel, assessment, onCreated }: LinkActivitiesModalProps) => {
  const { studyId } = useParams()
  const [form] = Form.useForm()
  const postAssessmentActivity = usePostAssessmentActivity(studyId, assessment?.id)

  // LOCAL state
  const detachProjects: number[] = Form.useWatch("detach_project_ids", form) || []
  const attachProjects: PayloadAttach[] = Form.useWatch("attach_projects", form) || []
  const createProjects: PayloadCreate[] = Form.useWatch("create_project_types", form) || []
  // selectValue used to clear the select input after selection
  const [selectValue, setSelectValue] = React.useState(null)
  const [formTouched, setFormTouched] = React.useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)

  // SERVER state
  const { data: activityTypes } = useGetActivityTypes()
  const { data: studyActivities } = useGetStudyActivities(studyId)

  // when assessment is selected, clear select and add linked activities
  React.useEffect(() => {
    form.resetFields()
    setSelectValue(null)
  }, [assessment])

  // disable submit button if no changes
  React.useEffect(() => {
    if (attachProjects.length || createProjects.length || detachProjects.length) {
      setFormTouched(true)
    } else {
      setFormTouched(false)
    }
  }, [attachProjects, createProjects, detachProjects])

  // LINK/UNLINK activity functions

  // find study activity from activities attached to assessment to get name and activity type
  const getLinkedStudyActivity = (id: number): StudyActivity | null => {
    if (!studyActivities) return null
    return studyActivities.find((activity: StudyActivity) => activity.id === id)
  }

  const getLinkedName = (id: number): string | null => {
    // name to be added
    const studyActivity = getLinkedStudyActivity(id)
    if (studyActivity?.project_title !== studyActivity?.project_type_title) return studyActivity?.project_title ?? null
    return null
  }

  const getLinkedStatus = (linkedId: number): ActivityStatus => {
    const linked = assessment.attached_projects.find((project) => project.id === linkedId)
    if (linked && linked.is_completed) {
      if (linked.progress_status === "cancelled") return "Cancelled"
      return "Completed"
    }
    return "In Progress"
  }

  // for to-detach state
  const isLinkedDetached = (linkedId: number): boolean => {
    return detachProjects.find((id: number) => id === linkedId) ? true : false
  }

  const toggleUnlink = (id: number, shouldLink: boolean) => {
    const activityFound = detachProjects.find((activityId) => activityId === id)
    if (shouldLink) {
      if (activityFound) {
        const newDetach = detachProjects.filter((activityId) => activityId !== id)
        form.setFieldValue("detach_project_ids", newDetach || [])
      }
    } else {
      if (!activityFound) {
        form.setFieldValue("detach_project_ids", [...detachProjects, id])
      }
    }
  }

  // ATTACH/CREATE activity functions

  // "created-id" or "new-id"
  const selectOptions = React.useMemo(() => {
    let options: ActivitySelectOption[] = []
    if (studyActivities && assessment && activityTypes) {
      // add activities associated to study
      studyActivities.forEach((activity: StudyActivity) => {
        if (
          // exclude cancelled activities
          activity.curr_state !== "Cancelled" &&
          // exclude linked activities
          !assessment.attached_projects.find((attached) => attached.id === activity.id) &&
          // exclude activities selected for linking
          !attachProjects.find((toAttach) => toAttach.project_id === activity.id)
        ) {
          options.push({
            value: `created-${activity.id}`,
            label: `${activity.project_type_title}${
              activity.project_type_title !== activity.project_title ? ` (${activity.project_title})` : ""
            }`,
          })
        }
      })
      // add activity types as new
      activityTypes.forEach((type: ActivityType) => {
        if (
          // exclude types of created activities that are in progress
          !studyActivities.find(
            (activity: StudyActivity) => activity.project_type_title === type.title && !activity.actual_completion_date
          ) &&
          // exclude activity types selected for creation
          !createProjects.find((selected) => selected.project_type_id === type.id)
        ) {
          options.push({
            value: `new-${type.id}`,
            label: type.title,
          })
        }
      })
    }
    return options.sort()
  }, [studyActivities, assessment, attachProjects, activityTypes, createProjects])

  const getSelectOptionStatus = (optionValue: string): ActivityStatus | "" => {
    if (optionValue.startsWith("new")) {
      return "New"
    } else if (optionValue.startsWith("created")) {
      const id = Number(optionValue.split("-")[1])
      const activityCompleted = studyActivities.find(
        (studyActivity: StudyActivity) => studyActivity.id === id
      )?.actual_completion_date
      if (activityCompleted) return "Completed"
      return "In Progress"
    }
    return ""
  }

  const addItem = (optionValue: string) => {
    if (optionValue) {
      const type = optionValue.split("-")[0] as ActivityTypeKey
      const id = Number(optionValue.split("-")[1])
      if (type === "new") {
        form.setFieldValue("create_project_types", [
          ...createProjects,
          {
            project_type_id: id,
            should_complete: false,
            name: null,
          },
        ])
      } else {
        form.setFieldValue("attach_projects", [
          ...attachProjects,
          {
            project_id: id,
            name: getLinkedName(id),
          },
        ])
      }
      setSelectValue(null)
    }
  }

  const onSubmitModal = (values: Payload) => {
    setIsSubmitting(true)
    postAssessmentActivity(
      {
        body: values,
      },
      {
        onSuccess: (res: { data: { project_ids: number[] } }) => {
          if (res?.data?.project_ids?.length) {
            // open up one newly created or linked activity in a new tab
            window.open(`/study/${studyId}/activity/${res.data.project_ids[0]}`, "_blank")
          }
          if (onCreated) onCreated()
        },
        onSettled: () => setIsSubmitting(false),
      }
    )
  }

  return (
    <ModalForm
      modalProps={{
        title: "Create/Link Activities",
        type: "create",
        open,
        onCancel,
        message: (
          <div className="link-activities-modal__message">
            {assessment?.assessment_name && (
              <div style={{ marginBottom: 16 }}>
                for <span style={{ fontWeight: 500 }}>{assessment.assessment_name}</span> assessment
              </div>
            )}
            {assessment?.project_type_ids?.length > 0 ? (
              <Alert
                message={
                  <div className="link-activities-modal__alert">
                    <strong>Recommended activities: </strong>{" "}
                    {assessment?.project_type_ids
                      ?.map((id) => {
                        return activityTypes.reduce((acc: string, type: ActivityType) => {
                          if (type.id === id) acc = type.title
                          return acc
                        }, "")
                      })
                      ?.join(assessment.operator === "|" ? " or " : " and ")}
                  </div>
                }
                type="info"
                className="link-activities-modal__alert"
              />
            ) : null}
          </div>
        ),
        className: "link-activities-modal",
        disabled: !formTouched,
        width: 700,
      }}
      formProps={{
        form,
        onFinish: onSubmitModal,
        initialValues: defaultSelectedActivities,
        disabled: isSubmitting,
      }}
    >
      <>
        {/* SELECT unlinked activities to link to this assessment */}
        <div className="link-activities-modal__select-wrapper">
          <Label>Activity</Label>
          <Select
            options={selectOptions}
            value={selectValue}
            optionRender={({ label, value }: ActivitySelectOption) => {
              return (
                <div className="link-activities-modal__select-option">
                  <div className="activity-select-option-label">
                    <span className="select-option-title">{label}</span>
                  </div>
                  <ActivityStatusBadge status={getSelectOptionStatus(value)} />
                </div>
              )
            }}
            onChange={(value: string) => addItem(value)}
          />
        </div>

        {/* SELECTED activity list */}
        <div className="link-activities-modal__list">
          {(assessment?.attached_projects.length > 0 || attachProjects.length > 0 || createProjects.length > 0) && (
            <Separator
              marginTop={8}
              marginBottom={10}
              className="link-activities-modal__separator"
            />
          )}

          {/* LINKED activities */}
          {(assessment?.attached_projects.length > 0 &&
            assessment?.attached_projects.map((linked: LinkedActivity) => (
              <ActivityListItem
                key={linked.id}
                type="linked"
                id={linked.id}
                title={getLinkedStudyActivity(linked.id)?.project_type_title}
                status={getLinkedStatus(linked.id)}
                name={getLinkedName(linked.id)}
                isLinked={!isLinkedDetached(linked.id)}
                onLink={toggleUnlink}
                disabled={isSubmitting}
              />
            ))) ||
            []}
          {/* always store even if empty array */}
          <Form.Item
            hidden
            name="detach_project_ids"
            initialValue={[]}
          />

          {/* ATTACH activities */}
          <Form.List name="attach_projects">
            {(fields, { remove }) => (
              <>
                {fields.map((field) => (
                  <Form.Item
                    key={field.key}
                    name={[field.name]}
                    rules={[
                      {
                        validator: (_, value) => {
                          {
                            if (
                              studyActivities.find(
                                (activity: StudyActivity) =>
                                  activity.project_title === value?.name && activity.id !== value.project_id
                              )
                            ) {
                              return Promise.reject(new Error(RENAME_ERROR_MSG))
                            }
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                    className="link-activities-modal__attach-form-item"
                  >
                    <AttachActivityItem
                      onRemove={() => remove(field.name)}
                      studyActivities={studyActivities}
                      disabled={isSubmitting}
                    />
                  </Form.Item>
                ))}
              </>
            )}
          </Form.List>

          {/* NEW activities */}
          <Form.List name="create_project_types">
            {(fields, { remove }) => (
              <>
                {fields.map((field) => (
                  <Form.Item
                    key={field.key}
                    name={[field.name]}
                    rules={[
                      {
                        validator: (_, value) => {
                          {
                            if (
                              studyActivities.find((activity: StudyActivity) => activity.project_title === value?.name)
                            ) {
                              return Promise.reject(new Error(RENAME_ERROR_MSG))
                            }
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                    className="link-activities-modal__attach-form-item"
                  >
                    <CreateActivityItem
                      remove={() => remove(field.name)}
                      activityTypes={activityTypes}
                      createProjects={createProjects}
                      disabled={isSubmitting}
                    />
                  </Form.Item>
                ))}
              </>
            )}
          </Form.List>
        </div>
      </>
    </ModalForm>
  )
}

export default LinkActivitiesModal
